import { atom } from 'jotai';
import { TranslationCache } from '../atoms/v0/translationAtoms';

export type ContentType = 'task_title' | 'task_desc' | 'project_title' | 'project_desc';

export class TranslationService {
  private static instance: TranslationService;
  private apiKey: string;
  private baseUrl = 'https://api.openai.com/v1/chat/completions';

  private constructor() {
    this.apiKey = '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';
  }

  public static getInstance(): TranslationService {
    if (!TranslationService.instance) {
      TranslationService.instance = new TranslationService();
    }
    return TranslationService.instance;
  }

  public getCacheKey(lang: string, contentType: ContentType, id: string): string {
    return `translation_${lang}_${contentType}_${id}`;
  }

  public async translateText(text: string, targetLang: string): Promise<string> {
    try {
      const response = await fetch(this.baseUrl, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${this.apiKey}`,
        },
        body: JSON.stringify({
          model: 'gpt-3.5-turbo',
          messages: [
            {
              role: 'system',
              content: `You are a professional translator. Translate the following text to ${targetLang}. Only output the translated text, nothing else.`
            },
            {
              role: 'user',
              content: text
            }
          ],
          temperature: 0.3,
          max_tokens: 1000,
        }),
      });

      if (!response.ok) {
        throw new Error(`Translation API error: ${response.statusText}`);
      }

      const data = await response.json();
      return data.choices[0].message.content.trim();
    } catch (error) {
      console.error('Translation failed:', error);
      throw error;
    }
  }

  public async getTranslation(
    text: string,
    targetLang: string,
    contentType: ContentType,
    id: string,
    cache: TranslationCache
  ): Promise<{ translatedText: string; newCache: TranslationCache }> {
    const cacheKey = `${text}_${targetLang}`;
    const cachedTranslation = cache[text]?.[targetLang];

    if (cachedTranslation) {
      console.log(`[TranslationService] Cache hit for: ${text} -> ${targetLang}`);
      return { translatedText: cachedTranslation, newCache: cache };
    }

    console.log(`[TranslationService] Cache miss, calling API for: ${text} -> ${targetLang}`);
    const translatedText = await this.translateText(text, targetLang);
    
    const updatedCacheEntry = { ...(cache[text] || {}), [targetLang]: translatedText };
    const newCache = { ...cache, [text]: updatedCacheEntry };

    return { translatedText, newCache };
  }

  public async batchTranslate(
    items: Array<{
      text: string;
      contentType: ContentType;
      id: string;
    }>,
    targetLang: string,
    cache: TranslationCache
  ): Promise<{
    results: Array<{ id: string; translatedText: string }>;
    newCache: TranslationCache;
  }> {
    const results: Array<{ id: string; translatedText: string }> = [];
    const newCache = { ...cache };

    // 先检查缓存
    for (const item of items) {
      const cacheKey = this.getCacheKey(targetLang, item.contentType, item.id);
      // 兼容新 cache 结构，cache[cacheKey]?.[targetLang]
      const cached = cache[cacheKey]?.[targetLang] || cache[cacheKey];
      if (cached && typeof cached === 'string') {
        results.push({ id: item.id, translatedText: cached });
      }
    }

    // 过滤出需要翻译的项
    const itemsToTranslate = items.filter(
      item => !results.some(r => r.id === item.id)
    );

    if (itemsToTranslate.length === 0) {
      return { results, newCache };
    }

    // 批量翻译
    try {
      const translatedTexts = await Promise.all(
        itemsToTranslate.map(item => this.translateText(item.text, targetLang))
      );

      // 更新缓存和结果
      translatedTexts.forEach((translatedText, index) => {
        const item = itemsToTranslate[index];
        const cacheKey = this.getCacheKey(targetLang, item.contentType, item.id);
        newCache[cacheKey] = { ...(newCache[cacheKey] || {}), [targetLang]: translatedText };
        results.push({ id: item.id, translatedText });
      });
    } catch (error) {
      console.error('Batch translation failed:', error);
      // 出错时返回原文
      itemsToTranslate.forEach(item => {
        results.push({ id: item.id, translatedText: item.text });
      });
    }

    return { results, newCache };
  }
} 